import React, { useState, useEffect } from 'react';
import {
  Dialog, 
  DialogTitle, 
  DialogContent, 
  DialogActions, 
  Button, 
  List, 
  ListItem, 
  ListItemText, 
  ListItemButton, 
  Radio, 
  Typography,
  Paper,
  Box,
  Divider,
  useTheme
} from '@mui/material';
import { Business as BusinessIcon } from '@mui/icons-material';
import { BranchRef } from '../../types/user';

interface BranchSelectionDialogProps {
  open: boolean;
  branches: BranchRef[];
  onSelect: (branchId: string) => void;
  defaultBranch?: string;
}

const BranchSelectionDialog: React.FC<BranchSelectionDialogProps> = ({
  open,
  branches,
  onSelect,
  defaultBranch
}) => {
  const [selectedBranch, setSelectedBranch] = useState<string>('');
  const theme = useTheme();

  useEffect(() => {
    // Set default branch if provided, otherwise select the first branch
    if (defaultBranch && branches.find(b => b._id === defaultBranch)) {
      setSelectedBranch(defaultBranch);
    } else if (branches.length > 0) {
      setSelectedBranch(branches[0]._id);
    }
    // Only run on initial mount and when branches or defaultBranch changes
  }, [branches, defaultBranch]);

  const handleConfirm = () => {
    if (selectedBranch) {
      onSelect(selectedBranch);
    }
  };

  return (
    <Dialog
      open={open}
      maxWidth="sm"
      fullWidth
      disableEscapeKeyDown
    >
      <Paper sx={{ 
        position: 'relative',
        overflow: 'hidden'
      }}>
        {/* Decorative header bar */}
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: 8,
            bgcolor: 'primary.main',
            background: 'linear-gradient(to right, #1976d2, #42a5f5)'
          }}
        />
        
        <DialogTitle sx={{ pt: 4, textAlign: 'center' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 1 }}>
            <BusinessIcon color="primary" fontSize="large" sx={{ mr: 1 }} />
            <Typography variant="h5" component="span" fontWeight="bold">
              اختيار الفرع
            </Typography>
          </Box>
          <Typography variant="subtitle1" color="text.secondary">
            اختر الفرع الذي ترغب في الدخول إليه
          </Typography>
        </DialogTitle>
        
        <Divider />
        
        <DialogContent sx={{ pt: 2 }}>
          <List sx={{ 
            width: '100%',
            bgcolor: 'background.paper',
            borderRadius: 2,
            border: `1px solid ${theme.palette.divider}`,
            overflow: 'hidden'
          }}>
            {branches.map((branch) => (
              <React.Fragment key={branch._id}>
                <ListItem disablePadding>
                  <ListItemButton
                    selected={selectedBranch === branch._id}
                    onClick={() => setSelectedBranch(branch._id)}
                    sx={{ 
                      py: 2,
                      '&.Mui-selected': {
                        bgcolor: 'rgba(25, 118, 210, 0.08)',
                      }
                    }}
                  >
                    <Radio
                      checked={selectedBranch === branch._id}
                      onChange={() => setSelectedBranch(branch._id)}
                      edge="start"
                    />
                    <ListItemText 
                      primary={
                        <Typography variant="subtitle1" fontWeight={selectedBranch === branch._id ? "bold" : "normal"}>
                          {branch.name}
                        </Typography>
                      } 
                    />
                    {branch._id === defaultBranch && (
                      <Box
                        sx={{
                          bgcolor: 'primary.light',
                          color: 'white',
                          px: 1.5,
                          py: 0.5,
                          fontSize: 12,
                          borderRadius: 1,
                          ml: 1
                        }}
                      >
                        الافتراضي
                      </Box>
                    )}
                  </ListItemButton>
                </ListItem>
                <Divider variant="fullWidth" component="li" />
              </React.Fragment>
            ))}
          </List>
        </DialogContent>
        
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button
            onClick={handleConfirm}
            variant="contained"
            size="large"
            fullWidth
            disabled={!selectedBranch}
            sx={{ py: 1 }}
          >
            تأكيد الدخول
          </Button>
        </DialogActions>
      </Paper>
    </Dialog>
  );
};

export default BranchSelectionDialog;